<template>
  <div class="app-container">
    <NavBar v-if="$route.meta.showNavBar"/>
    <Sidebar
      :isCollapsed="isCollapsed"
      :isSidebarVisible="isSidebarVisible"
      @toggle-collapse="toggleCollapse"
      @toggle-sidebar="toggleSidebar"
      v-if="$route.meta.showNavBar"
    />

    <div :class="[
      'layout-content-wrapper',
      {
        'layout-content-with-sidebar': $route.meta.showNavBar && (isSidebarVisible || !isMobile) && !isCollapsed,
        'layout-content-with-collapsed-sidebar': $route.meta.showNavBar && (isSidebarVisible || !isMobile) && isCollapsed,
        'layout-content-no-sidebar': $route.meta.showNavBar && !isSidebarVisible && isMobile
      },
      { 'mt-4 md:mt-2 lg:mt-14 xl:mt-16': $route.meta.showNavBar, 'mt-0': !$route.meta.showNavBar }
    ]">
      <div :class="['layout-content ml-auto mr-auto', { 'dashboard-layout': $route.name === 'dashboard' }]">
                 <router-view :class="[$route.meta.showNavBar && $route.name !== 'dashboard' ? 'p-2 pt-2 md:p-4 md:pt-3 lg:p-6 lg:pt-5' : 'pl-2 pr-2 md:pl-4 md:pr-4 lg:pl-6 lg:pr-6']"></router-view>
        <canvas id="canvas" v-show="$route.meta.showCanvasBG"></canvas>
      </div>
    </div>
    <Footer />
  </div>
</template>

<script>
import NavBar from '@/components/NavBar.vue';
import Sidebar from '@/components/Sidebar.vue';
import Footer from '@/components/Footer.vue';
import {ref, onMounted, computed, watch} from 'vue';
import {useRouter, useRoute} from 'vue-router';
import {useStore} from 'vuex';

import api from '@/utils/axios';

function initBG() {
  const canvas = document.getElementById("canvas");
  if (!canvas) return;

  const ctx = canvas.getContext('2d');

  canvas.width = window.innerWidth;
  canvas.height = window.innerHeight;

  var stars = [],
      FPS = 60,
      x = 100,
      mouse = {
        x: 0,
        y: 0
      };

  for (var i = 0; i < x; i++) {
    stars.push({
      x: Math.random() * canvas.width,
      y: Math.random() * canvas.height,
      radius: Math.random() * 1 + 1,
      vx: Math.floor(Math.random() * 50) - 25,
      vy: Math.floor(Math.random() * 50) - 25
    });
  }

  function draw() {
    ctx.clearRect(0, 0, canvas.width, canvas.height);

    ctx.globalCompositeOperation = "lighter";

    for (var i = 0, x = stars.length; i < x; i++) {
      var s = stars[i];

      ctx.fillStyle = "#4ade80";
      ctx.beginPath();
      ctx.arc(s.x, s.y, s.radius, 0, 2 * Math.PI);
      ctx.fill();
      ctx.fillStyle = '#4ade80';
      ctx.stroke();
    }

    ctx.beginPath();
    for (var i = 0, x = stars.length; i < x; i++) {
      var starI = stars[i];
      ctx.moveTo(starI.x, starI.y);
      if (distance(mouse, starI) < 150) ctx.lineTo(mouse.x, mouse.y);
      for (var j = 0, x = stars.length; j < x; j++) {
        var starII = stars[j];
        if (distance(starI, starII) < 150) {
          ctx.lineTo(starII.x, starII.y);
        }
      }
    }
    ctx.lineWidth = 0.1;
    ctx.strokeStyle = 'gray';
    ctx.stroke();
  }

  function distance(point1, point2) {
    var xs = 0;
    var ys = 0;

    xs = point2.x - point1.x;
    xs = xs * xs;

    ys = point2.y - point1.y;
    ys = ys * ys;

    return Math.sqrt(xs + ys);
  }

  function update() {
    for (var i = 0, x = stars.length; i < x; i++) {
      var s = stars[i];

      s.x += s.vx / FPS;
      s.y += s.vy / FPS;

      if (s.x < 0 || s.x > canvas.width) s.vx = -s.vx;
      if (s.y < 0 || s.y > canvas.height) s.vy = -s.vy;
    }
  }

  canvas.addEventListener('mousemove', function (e) {
    mouse.x = e.clientX;
    mouse.y = e.clientY;
  });

  function tick() {
    draw();
    update();
    requestAnimationFrame(tick);
  }

  tick();
}

export default {
  name: 'App',
  components: {NavBar, Sidebar, Footer},
  setup() {
    const store = useStore();
    const router = useRouter();
    const route = useRoute();
    const isCollapsed = ref(true);
    const isSidebarVisible = ref(false);
    const isMobile = ref(window.innerWidth < 992);

    const checkUser = async () => {
      try {
        const token = localStorage.getItem('lbToken');
        const isLogged = localStorage.getItem('lbIsLogged');

        if (route.name === 'login' && (!token || isLogged !== 'true')) {
          return;
        }

        if (!token || isLogged !== 'true') {
          if (route.name !== 'login') {
            // Сохраняем текущий путь для восстановления после логина
            const currentPath = route.fullPath;
            if (currentPath !== '/' && currentPath !== '/login') {
              localStorage.setItem('redirectAfterLogin', currentPath);
            }

            router.push({name: 'login'});
          }
          return;
        }

        if (store.state.users.isValidating) {
          return;
        }

        // Инициализируем авторизацию пользователя
        await store.dispatch('users/initializeAuth');
        
        // После успешной инициализации проверяем, нужно ли восстановить путь
        const savedPath = localStorage.getItem('redirectAfterLogin');
        if (savedPath && route.name === 'login') {
          localStorage.removeItem('redirectAfterLogin');

          router.push(savedPath);
        }
      } catch (e) {
        console.error('Error checking user:', e);
      }
    }

    const toggleCollapse = () => {
      isCollapsed.value = !isCollapsed.value;
      store.commit('modals/toggleCollapse');
    };

    const toggleSidebar = () => {
      isSidebarVisible.value = !isSidebarVisible.value;
      store.commit('modals/toggleSidebar');
    };

    // Handle resize to adjust sidebar state
    const handleResize = () => {
      const newIsMobile = window.innerWidth < 992;

      if (newIsMobile !== isMobile.value) {
        isMobile.value = newIsMobile;
        store.commit('modals/setSidebarMobile', newIsMobile);

        if (newIsMobile) {
          // При переходе на мобильный - скрываем sidebar и сворачиваем
          isSidebarVisible.value = false;
          isCollapsed.value = true;
          store.commit('modals/setSidebarVisible', false);
          store.commit('modals/setSidebarCollapsed', true);
        } else {
          // При переходе на десктоп - показываем но оставляем свернутым
          isSidebarVisible.value = true;
          isCollapsed.value = true;
          store.commit('modals/setSidebarVisible', true);
          store.commit('modals/setSidebarCollapsed', true);
        }
      }
    };

    onMounted(() => {
      // Initialize theme
      const isTheme = localStorage.getItem('isTheme');
      if (isTheme === 'dark') {
        document.documentElement.classList.add("dark");
      } else {
        document.documentElement.classList.remove("dark");
      }

      // Инициализация состояния sidebar в хранилище
      const initialIsMobile = window.innerWidth < 992;
      isMobile.value = initialIsMobile;
      store.commit('modals/setSidebarMobile', initialIsMobile);

      if (initialIsMobile) {
        // На мобильных устройствах по умолчанию скрыт и свернут
        isSidebarVisible.value = false;
        isCollapsed.value = true;
        store.commit('modals/setSidebarVisible', false);
        store.commit('modals/setSidebarCollapsed', true);
      } else {
        // На десктопе по умолчанию виден но свернут
        isSidebarVisible.value = true;
        isCollapsed.value = true;
        store.commit('modals/setSidebarVisible', true);
        store.commit('modals/setSidebarCollapsed', true);
      }

      // Initialize background
      try {
        initBG();
      } catch (e) {
      }

      // Check user
      checkUser();

      // Initialize theme in store
      store.dispatch('initializeTheme');

      // Add resize listener
      window.addEventListener('resize', handleResize);
    });

    // Синхронизация с состоянием в хранилище
    watch(() => store.state.modals.sidebar.isCollapsed, (newVal) => {
      isCollapsed.value = newVal;
    });

    watch(() => store.state.modals.sidebar.isSidebarVisible, (newVal) => {
      isSidebarVisible.value = newVal;
    });

    return {
      isCollapsed,
      isSidebarVisible,
      isMobile,
      toggleCollapse,
      toggleSidebar
    };
  },
  mounted() {
  }
}
</script>

<style>
.app-container {
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  position: relative;
  overflow-x: hidden;
}

.layout-content-wrapper {
  flex: 1;
  transition: margin-left 0.3s ease-in-out;
  will-change: margin-left;
  }

  @media screen and (min-width: 1472px) {
    .layout-content {
      max-width: 1572px;
      margin-right: auto !important;
    }

    /* Исключение для дашборда - убираем ограничение ширины */
    .layout-content.dashboard-layout {
      max-width: none !important;
      margin-right: 0 !important;
    }
  }

  /* Content margins for sidebar */
.layout-content-with-sidebar {
  margin-left: 250px; /* Width of expanded sidebar */
  transition: margin-left 0.3s ease-in-out;
  will-change: margin-left;
}

.layout-content-with-collapsed-sidebar {
  margin-left: 70px; /* Width of collapsed sidebar */
  transition: margin-left 0.3s ease-in-out;
  will-change: margin-left;
}

.layout-content-no-sidebar {
  margin-left: 0; /* No sidebar on mobile */
  transition: margin-left 0.3s ease-in-out;
  will-change: margin-left;
}

@media screen and (max-width: 991px) {
  .layout-content-with-sidebar,
  .layout-content-with-collapsed-sidebar {
    margin-left: 70px; /* On mobile, always use collapsed width when visible */
    transition: margin-left 0.3s ease-in-out;
    will-change: margin-left;
  }

  .layout-content-no-sidebar {
    margin-left: 0; /* No margin when sidebar is hidden */
    transition: margin-left 0.3s ease-in-out;
    will-change: margin-left;
  }
}

.p-4 {
  padding: 1rem;
}
</style>
